import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useSessao } from "@/sessao/useSessao";
import { useVisaoGeral } from "@/api/consultas";
import type { VisaoGeral as Visao } from "@/api/contrato";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { hojeUTC } from "@/calendario";
import { competencia, contagem, dia, dinheiro } from "@/formato";
import {
  ROTA_DAS_APLICACOES,
  ROTA_DAS_FATURAS,
  rotaDaOperacao,
  rotaDosBoletos,
  rotaDosConvenios,
} from "@/rotas";
import {
  FitaDeOperacao,
  filaMortaSemContagem,
  mensagensNaFilaMorta,
  textoDoEstado,
} from "./FitaDeOperacao";

/*
 * A tela inicial: pilha de gravidade (doc 06a §4.2), com os alertas em cima e os números
 * embaixo. O que está errado, em ordem, e o caminho para resolver.
 *
 * Sem os gráficos e sem o fluxo de eventos do doc 06a: a API devolve agregado da janela, sem
 * série (design D6), e a fita mostra esse agregado.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O ALERTA DIZ ONDE IR. O NÚMERO MORA NA CAIXA OU NA FITA.                 │
 * │                                                                          │
 * │ Um bloco só, de nomes curtos: uma faixa por alerta empurrava os números  │
 * │ para baixo da dobra, repetindo o que a caixa logo abaixo já mostra.      │
 * │ Quem não vê a caixa ao lado recebe o fato inteiro no nome do link.       │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Estado normal não se anuncia: sem alerta, a pilha some. A linha "Nenhum alerta ativo" do
 * doc 06a §4.2 ficou só para leitor de tela, como em `PilulaDeExcecao`: à vista, ela diria
 * "está tudo bem" também com uma fonte fora.
 */
export function VisaoGeral() {
  const { cliente, estado } = useSessao();
  const { data, error, isPending } = useVisaoGeral(cliente, estado.fase === "dentro");

  if (isPending) return <EsqueletoDaTela />;

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <h1 className="text-titulo font-semibold">Visão geral</h1>
        {data !== undefined && (
          <span className="rotulo">
            medida em {new Date(data.medida_em).toLocaleTimeString("pt-BR")}
          </span>
        )}
      </header>

      {/*
        A releitura que falha mantém `data`, e o "medida em" diz de quando ela é. Trocar a tela
        pela faixa tiraria os alertas de vista num soluço de rede, a cada dez segundos.
      */}
      {error !== null && <FaixaDeErro erro={error} />}
      {data !== undefined && <Medicao visao={data} />}
    </section>
  );
}

const GRADE_DE_CAIXAS = "grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5";

/* Com a altura de quem substitui (doc 06a §7): nenhum salto quando a medição chega. */
function EsqueletoDaTela() {
  return (
    <section aria-busy="true" className="flex flex-col gap-3">
      <Esqueleto className="h-[22px] w-24" />
      <div className={GRADE_DE_CAIXAS}>
        {Array.from({ length: 11 }, (_, indice) => (
          <Esqueleto key={indice} className="h-[55px]" />
        ))}
      </div>
      <Esqueleto className="h-[60px]" />
    </section>
  );
}

function Medicao({ visao }: { visao: Visao }) {
  const alertas = alertasDe(visao);

  return (
    <>
      {alertas.length === 0 ? (
        <p className="sr-only">Nenhum alerta ativo.</p>
      ) : (
        <BlocoDeAlertas alertas={alertas} />
      )}

      <div className={GRADE_DE_CAIXAS}>
        <CaixaDeCampo rotulo="Indeterminados" dados>
          {valorOuEstado(visao.indeterminados)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Títulos órfãos" dados>
          {valorOuEstado(visao.titulos_orfaos)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Envio incerto" dados>
          {valorOuEstado(visao.envio_incerto)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Sem andamento" dados>
          {valorOuEstado(visao.sem_andamento)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Circuitos abertos" dados>
          {visao.circuitos?.estado === "ok"
            ? String(visao.circuitos.abertos?.length ?? 0)
            : textoDoEstado(visao.circuitos?.estado)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Atraso do relay" dados>
          {visao.outbox?.estado === "ok"
            ? `${visao.outbox.segundos ?? 0}s`
            : textoDoEstado(visao.outbox?.estado)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Divergências abertas" dados>
          {valorOuEstado(visao.divergencias)}
        </CaixaDeCampo>
        {/* Sem alerta: a lacuna é deliberada, e nenhuma lista a resolve (design D7). */}
        <CaixaDeCampo rotulo="Números em quarentena" dados>
          {valorOuEstado(visao.numeros_em_quarentena)}
        </CaixaDeCampo>
        {/* Sem alerta, a caixa é o que separa "nenhuma aplicação" de "a contagem caiu". */}
        <CaixaDeCampo rotulo="Webhook sem segredo" dados>
          {valorOuEstado(visao.entrega_sem_segredo)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Certificados vencidos" dados>
          {visao.certificados?.estado === "ok"
            ? `${visao.certificados.vencidos ?? 0} · ${visao.certificados.vencendo ?? 0} vencendo`
            : textoDoEstado(visao.certificados?.estado)}
        </CaixaDeCampo>
        <CaixaDeCampo rotulo="Faturas vencidas" dados>
          {visao.faturamento?.estado === "ok"
            ? `${visao.faturamento.vencidas_sem_baixa ?? 0} · ${dinheiro(visao.faturamento.total_vencido_centavos ?? 0)}`
            : textoDoEstado(visao.faturamento?.estado)}
        </CaixaDeCampo>
      </div>

      <FitaDeOperacao visao={visao} />
    </>
  );
}

function BlocoDeAlertas({ alertas }: { alertas: readonly Alerta[] }) {
  const grave = alertas.some((alerta) => alerta.grave);

  return (
    <div
      className={[
        "flex items-start gap-2 rounded-cartao border bg-superficie px-4 py-2",
        grave ? "border-indeterminado" : "border-transito/40",
      ].join(" ")}
    >
      <AlertTriangle
        aria-hidden="true"
        className={`mt-0.5 size-4 shrink-0 ${grave ? "text-indeterminado" : "text-transito"}`}
      />
      <ul aria-label="Alertas" className="flex flex-wrap items-center gap-x-5 gap-y-1">
        {alertas.map((alerta) => (
          <li key={alerta.chave} className="flex items-center gap-1">
            <Link
              to={alerta.para}
              aria-label={alerta.fato}
              className={[
                "underline decoration-fio-forte underline-offset-2 hover:decoration-current",
                alerta.grave ? "font-medium text-indeterminado" : "text-texto",
              ].join(" ")}
            >
              {alerta.rotulo}
            </Link>
            {alerta.dica !== undefined && <Dica texto={alerta.dica} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface Alerta {
  chave: string;
  /** O nome curto, à vista. O número mora na caixa ou na fita, e não se repete aqui. */
  rotulo: string;
  /** O fato inteiro, com o número, para quem não vê a caixa ao lado: o nome do link. */
  fato: string;
  /** Grave usa o magenta do indeterminado — reservado ao que é dinheiro em risco. */
  grave: boolean;
  /** A tela onde o alerta se resolve. Obrigatório: alerta sem saída só assusta. */
  para: string;
  /** Só onde o rótulo não diz a consequência. O resto se explica, ou se explica no destino. */
  dica?: string;
}

/** O rótulo acompanha o número de `fato`: o nome do link precisa conter o que se lê. */
function umOuVarios(quantos: number, um: string, varios: string): string {
  return quantos === 1 ? um : varios;
}

/*
 * A ordem é de GRAVIDADE, e não de bonito (doc 06 §4.1).
 *
 * Indeterminado primeiro, sempre: é dinheiro em estado desconhecido, e é a razão
 * pela qual esta tela existe.
 *
 * Nenhum alerta sai de indicador fora de `ok`: o que não se mediu não vira alerta nem zero,
 * e quem diz "não foi possível medir" é a caixa ou a fita.
 */
function alertasDe(visao: Visao): Alerta[] {
  const alertas: Alerta[] = [];

  const indeterminados = visao.indeterminados;
  if (indeterminados?.estado === "ok" && (indeterminados.valor ?? 0) > 0) {
    const quantos = indeterminados.valor ?? 0;
    alertas.push({
      chave: "indeterminados",
      grave: true,
      para: rotaDaOperacao("indeterminados"),
      rotulo: umOuVarios(quantos, "Boleto indeterminado", "Boletos indeterminados"),
      fato: `${contagem(quantos, "boleto indeterminado", "boletos indeterminados")}.`,
    });
  }

  /*
   * Logo abaixo do indeterminado, e no mesmo magenta: o órfão é a cara de uma duplicidade
   * consumada, e o envio incerto é dinheiro em estado desconhecido antes das 96 horas. O link
   * do envio abre `envio_em_aberto`, e não `envio_estado=incerto`: a contagem inclui a lease
   * vencida, e o número do alerta é a lista que ele abre.
   */
  const orfaos = visao.titulos_orfaos;
  if (orfaos?.estado === "ok" && (orfaos.valor ?? 0) > 0) {
    const quantos = orfaos.valor ?? 0;
    alertas.push({
      chave: "titulos-orfaos",
      grave: true,
      para: rotaDaOperacao("titulos-orfaos"),
      rotulo: umOuVarios(quantos, "Título órfão", "Títulos órfãos"),
      fato: `${contagem(quantos, "título órfão", "títulos órfãos")} no provider.`,
      dica: "Título no provider sem boleto nosso. Pode ser cobrança duplicada.",
    });
  }

  const envioIncerto = visao.envio_incerto;
  if (envioIncerto?.estado === "ok" && (envioIncerto.valor ?? 0) > 0) {
    const quantos = envioIncerto.valor ?? 0;
    alertas.push({
      chave: "envio-incerto",
      grave: true,
      para: rotaDosBoletos({ envio_em_aberto: "true" }),
      rotulo: umOuVarios(quantos, "Envio incerto", "Envios incertos"),
      fato: `${contagem(quantos, "boleto", "boletos")} com envio incerto.`,
      dica: "Pode existir título no provider, e a situação ainda diz ENFILEIRADO.",
    });
  }

  const circuitos = visao.circuitos;
  if (circuitos?.estado === "ok" && (circuitos.abertos?.length ?? 0) > 0) {
    const quantos = circuitos.abertos?.length ?? 0;
    alertas.push({
      chave: "circuitos",
      grave: false,
      para: rotaDaOperacao("provider"),
      rotulo: umOuVarios(quantos, "Circuito aberto", "Circuitos abertos"),
      fato: `${contagem(quantos, "circuito aberto", "circuitos abertos")}.`,
    });
  }

  const filas = visao.filas;
  if (filas?.estado === "ok" && mensagensNaFilaMorta(filas) > 0) {
    const mensagens = `${contagem(mensagensNaFilaMorta(filas), "mensagem", "mensagens")} na fila morta`;
    alertas.push({
      chave: "fila-morta",
      grave: false,
      para: rotaDaOperacao("fila-morta"),
      // Sem contagem confiável, a fita diz que não mediu, e o parcial só cabe aqui.
      rotulo: filaMortaSemContagem(filas) ? mensagens : "Fila morta",
      fato: `${mensagens}.`,
      dica: "Mensagens que esgotaram as tentativas. Pode haver boleto preso, sem desfecho.",
    });
  }

  const semAndamento = visao.sem_andamento;
  if (semAndamento?.estado === "ok" && (semAndamento.valor ?? 0) > 0) {
    const quantos = semAndamento.valor ?? 0;
    alertas.push({
      chave: "sem-andamento",
      grave: false,
      para: rotaDosBoletos({ presos: "true" }),
      rotulo: umOuVarios(quantos, "Boleto sem andamento", "Boletos sem andamento"),
      fato: `${contagem(quantos, "boleto", "boletos")} sem andamento.`,
    });
  }

  const outbox = visao.outbox;
  if (outbox?.estado === "ok" && (outbox.segundos ?? 0) > 60) {
    alertas.push({
      chave: "outbox",
      grave: false,
      para: rotaDaOperacao("provider"),
      rotulo: "Relay atrasado",
      fato: `Relay atrasado: ${outbox.segundos ?? 0}s.`,
    });
  }

  const divergencias = visao.divergencias;
  if (divergencias?.estado === "ok" && (divergencias.valor ?? 0) > 0) {
    const quantas = divergencias.valor ?? 0;
    alertas.push({
      chave: "divergencias",
      grave: false,
      para: rotaDaOperacao("divergencias"),
      rotulo: umOuVarios(quantas, "Divergência", "Divergências"),
      fato: `${contagem(quantas, "divergência", "divergências")} com o provider.`,
    });
  }

  const semSegredo = visao.entrega_sem_segredo;
  if (semSegredo?.estado === "ok" && (semSegredo.valor ?? 0) > 0) {
    alertas.push({
      chave: "webhook-sem-segredo",
      grave: false,
      para: ROTA_DAS_APLICACOES,
      rotulo: "Webhook sem segredo",
      fato: `${contagem(semSegredo.valor ?? 0, "aplicação", "aplicações")} com webhook sem segredo.`,
      dica: "Os eventos novos são descartados, sem erro nenhum.",
    });
  }

  /*
   * Antes do faturamento: o certificado vencido barra a emissão daquele convênio agora, e o
   * que vence em 30 dias ainda dá tempo. O número é o total da lista que o link abre.
   */
  const certificados = visao.certificados;
  if (certificados?.estado === "ok") {
    const vencidos = certificados.vencidos ?? 0;
    if (vencidos > 0) {
      alertas.push({
        chave: "certificados-vencidos",
        grave: true,
        para: rotaDosConvenios({ certificado: "vencido" }),
        rotulo: umOuVarios(vencidos, "Certificado vencido", "Certificados vencidos"),
        fato: `${contagem(vencidos, "certificado vencido", "certificados vencidos")}.`,
        dica: "A emissão recusa boleto desses convênios até o certificado ser trocado.",
      });
    }

    const vencendo = certificados.vencendo ?? 0;
    if (vencendo > 0) {
      alertas.push({
        chave: "certificados-vencendo",
        grave: false,
        para: rotaDosConvenios({ certificado: "vencendo" }),
        rotulo: umOuVarios(vencendo, "Certificado vencendo", "Certificados vencendo"),
        fato: `${contagem(vencendo, "certificado vencendo", "certificados vencendo")}.`,
      });
    }
  }

  /*
   * O faturamento por último: nenhum dos dois é incidente do motor, e o dinheiro
   * já está em fatura gravada. Mas o motor não tem cron (doc 01 §7) — o
   * fechamento é manual, e se esta linha não disser que outubro ficou aberto,
   * ninguém mais diz.
   */
  const faturamento = visao.faturamento;
  if (faturamento?.estado === "ok") {
    const pendente = faturamento.competencia_pendente ?? null;
    if (pendente !== null) {
      const liberadoEm = faturamento.fechamento_liberado_em ?? null;
      const rotulo =
        liberadoEm !== null && liberadoEm > hojeUTC()
          ? `Competência ${competencia(pendente)} a fechar a partir de ${dia(liberadoEm)}`
          : `Competência ${competencia(pendente)} pendente de fechamento`;

      alertas.push({
        chave: "competencia-pendente",
        grave: false,
        para: ROTA_DAS_FATURAS,
        rotulo,
        fato: `${rotulo}.`,
      });
    }

    const vencidas = faturamento.vencidas_sem_baixa ?? 0;
    if (vencidas > 0) {
      alertas.push({
        chave: "faturas-vencidas",
        grave: false,
        para: `${ROTA_DAS_FATURAS}?aba=fechadas&vencidas=true`,
        rotulo: umOuVarios(vencidas, "Fatura vencida", "Faturas vencidas"),
        fato: `${contagem(vencidas, "fatura vencida", "faturas vencidas")} sem baixa: ${dinheiro(faturamento.total_vencido_centavos ?? 0)}.`,
      });
    }
  }

  return alertas;
}

function valorOuEstado(indicador: Visao["indeterminados"]): string {
  return indicador?.estado === "ok"
    ? String(indicador.valor ?? 0)
    : textoDoEstado(indicador?.estado);
}
